import { supabase } from './supabase';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
    public raw?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Active marina context (set by useAuth) ─────────────────────
// org_admin injects ?marinaId= on every scoped request via this variable.
let _activeMarinaId: number | null = null;

export function setActiveMarinaIdContext(id: number | null): void {
  _activeMarinaId = id;
}

async function getAuthToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiError('Not authenticated', 401, 'AUTH_REQUIRED');
  return token;
}

function appendMarinaId(path: string): string {
  if (_activeMarinaId === null) {
    return path;
  }
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}marinaId=${_activeMarinaId}`;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  skipMarinaId = false,
): Promise<T> {
  const token = await getAuthToken();
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (body) headers['Content-Type'] = 'application/json';

  const resolvedPath = skipMarinaId ? path : appendMarinaId(path);

  const res = await fetch(`${API_BASE}/api/v1/admin${resolvedPath}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 || res.status === 403) {
    if (res.status === 401) {
      await supabase.auth.signOut();
      window.location.href = '/login';
    }
    let message = res.status === 403 ? 'Access denied' : 'Session expired';
    if (res.status === 403) {
      try {
        const errBody = (await res.json()) as { error?: string; code?: string };
        if (typeof errBody.error === 'string' && errBody.error.length > 0) {
          message = errBody.error;
        }
      } catch {
        /* keep default */
      }
    }
    throw new ApiError(
      message,
      res.status,
      res.status === 403 ? 'FORBIDDEN' : 'AUTH_EXPIRED',
    );
  }

  if (res.status === 204) {
    return undefined as T;
  }

  let json: Record<string, unknown>;
  try {
    json = await res.json();
  } catch {
    throw new ApiError(`Server error (${res.status})`, res.status, 'PARSE_ERROR');
  }

  if (!res.ok) {
    throw new ApiError(
      (json.error as string) ?? 'Request failed',
      res.status,
      (json.code as string) ?? 'UNKNOWN',
      json,
    );
  }

  return json as T;
}

// ── Public (no marina scoping) ────────────────────────────────
async function publicRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${API_BASE}/api/v1${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  let json: Record<string, unknown>;
  try {
    json = await res.json();
  } catch {
    throw new ApiError(`Server error (${res.status})`, res.status, 'PARSE_ERROR');
  }

  if (!res.ok) {
    throw new ApiError(
      (json.error as string) ?? 'Request failed',
      res.status,
      (json.code as string) ?? 'UNKNOWN',
    );
  }
  return json as T;
}

async function handleAdminSessionErrors(res: Response): Promise<void> {
  if (res.status === 401 || res.status === 403) {
    if (res.status === 401) {
      await supabase.auth.signOut();
      window.location.href = '/login';
    }
    let message = res.status === 403 ? 'Access denied' : 'Session expired';
    if (res.status === 403) {
      try {
        const errBody = (await res.json()) as { error?: string };
        if (typeof errBody.error === 'string' && errBody.error.length > 0) {
          message = errBody.error;
        }
      } catch {
        /* keep default */
      }
    }
    throw new ApiError(message, res.status, res.status === 403 ? 'FORBIDDEN' : 'AUTH_EXPIRED');
  }
}

/** Multipart upload — field name must be `file` (JPEG/PNG/WebP/GIF, max 2 MB). */
export async function uploadBerthPlanImage(file: File): Promise<{ berth_plan_image_url: string }> {
  const token = await getAuthToken();
  const resolvedPath = appendMarinaId('/marina/berth-plan-image');
  const body = new FormData();
  body.append('file', file);
  const res = await fetch(`${API_BASE}/api/v1/admin${resolvedPath}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  await handleAdminSessionErrors(res);
  let json: Record<string, unknown>;
  try {
    json = await res.json();
  } catch {
    throw new ApiError(`Server error (${res.status})`, res.status, 'PARSE_ERROR');
  }
  if (!res.ok) {
    throw new ApiError(
      (json.error as string) ?? 'Upload failed',
      res.status,
      (json.code as string) ?? 'UNKNOWN',
      json,
    );
  }
  return json as { berth_plan_image_url: string };
}

export async function deleteBerthPlanImage(): Promise<{ berth_plan_image_url: null }> {
  const token = await getAuthToken();
  const resolvedPath = appendMarinaId('/marina/berth-plan-image');
  const res = await fetch(`${API_BASE}/api/v1/admin${resolvedPath}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  await handleAdminSessionErrors(res);
  let json: Record<string, unknown>;
  try {
    json = await res.json();
  } catch {
    throw new ApiError(`Server error (${res.status})`, res.status, 'PARSE_ERROR');
  }
  if (!res.ok) {
    throw new ApiError(
      (json.error as string) ?? 'Request failed',
      res.status,
      (json.code as string) ?? 'UNKNOWN',
      json,
    );
  }
  return json as { berth_plan_image_url: null };
}

// ── API object ────────────────────────────────────────────────

export const api = {
  // ── Current user
  getMe: () => request<Record<string, unknown>>('GET', '/me', undefined, true),

  // ── Marina config
  getMarina: () => request<Record<string, unknown>>('GET', '/marina'),
  updateMarina: (data: Record<string, unknown>) => request<Record<string, unknown>>('PUT', '/marina', data),

  // ── Berths
  getBerths: () => request<{ berths: Record<string, unknown>[] }>('GET', '/berths'),
  createBerth: (data: Record<string, unknown>) => request<Record<string, unknown>>('POST', '/berths', data),
  updateBerth: (id: number, data: Record<string, unknown>) => request<Record<string, unknown>>('PUT', `/berths/${id}`, data),
  deleteBerth: (id: number, options?: { permanent?: boolean }) =>
    request<Record<string, unknown> | undefined>(
      'DELETE',
      `/berths/${id}${options?.permanent ? '?permanent=1' : ''}`,
    ),

  // ── Parking (module)
  getParkingSpots: () => request<{ spots: Record<string, unknown>[] }>('GET', '/parking-spots'),
  createParkingSpot: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('POST', '/parking-spots', data),
  updateParkingSpot: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('PUT', `/parking-spots/${id}`, data),
  deleteParkingSpot: (id: number, options?: { permanent?: boolean }) =>
    request<Record<string, unknown> | undefined>(
      'DELETE',
      `/parking-spots/${id}${options?.permanent ? '?permanent=1' : ''}`,
    ),

  getParkingBookings: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<{ bookings: Record<string, unknown>[]; total: number }>('GET', `/parking-bookings${qs}`);
  },
  createParkingBooking: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('POST', '/parking-bookings', data),
  updateParkingBookingStatus: (id: number, status: string) =>
    request<Record<string, unknown>>('PUT', `/parking-bookings/${id}/status`, { status }),
  updateParkingBooking: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('PUT', `/parking-bookings/${id}`, data),

  getParkingPricingRules: () =>
    request<{ rules: Record<string, unknown>[] }>('GET', '/parking-pricing-rules'),
  createParkingPricingRule: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('POST', '/parking-pricing-rules', data),
  updateParkingPricingRule: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('PUT', `/parking-pricing-rules/${id}`, data),
  deleteParkingPricingRule: (id: number) => request<void>('DELETE', `/parking-pricing-rules/${id}`),

  // ── Bookings
  getBookings: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<{ bookings: Record<string, unknown>[]; total: number }>('GET', `/bookings${qs}`);
  },
  createBooking: (data: Record<string, unknown>) => request<Record<string, unknown>>('POST', '/bookings', data),
  updateBooking: (id: string, data: Record<string, unknown>) => request<Record<string, unknown>>('PUT', `/bookings/${id}`, data),
  updateBookingStatus: (id: string, status: string) => request<Record<string, unknown>>('PUT', `/bookings/${id}/status`, { status }),
  updateBookingBerth: (id: string, berthId: number) => request<Record<string, unknown>>('PUT', `/bookings/${id}/berth`, { berth_id: berthId }),
  deleteBooking: (id: string) => request<void>('DELETE', `/bookings/${id}`),

  // ── Pricing
  getPricing: () => request<{ rules: Record<string, unknown>[] }>('GET', '/pricing'),
  createPricingRule: (data: Record<string, unknown>) => request<Record<string, unknown>>('POST', '/pricing', data),
  updatePricingRule: (id: number, data: Record<string, unknown>) => request<Record<string, unknown>>('PUT', `/pricing/${id}`, data),
  deletePricingRule: (id: number) => request<void>('DELETE', `/pricing/${id}`),

  // ── Knowledge
  getKnowledge: () => request<{ entries: Record<string, unknown>[] }>('GET', '/knowledge'),
  createKnowledge: (data: { section: string; language: string; content: string }) => request<Record<string, unknown>>('POST', '/knowledge', data),
  updateKnowledge: (id: number, data: Record<string, unknown>) => request<Record<string, unknown>>('PUT', `/knowledge/${id}`, data),
  deleteKnowledge: (id: number) => request<void>('DELETE', `/knowledge/${id}`),

  // ── Access codes
  getAccessCodes: () => request<{ codes: Record<string, unknown>[] }>('GET', '/access-codes'),
  createAccessCode: (data: Record<string, unknown>) => request<Record<string, unknown>>('POST', '/access-codes', data),
  updateAccessCode: (id: number, data: Record<string, unknown>) => request<Record<string, unknown>>('PUT', `/access-codes/${id}`, data),
  deleteAccessCode: (id: number) => request<void>('DELETE', `/access-codes/${id}`),

  // ── Additional services (sauna, bikes, etc.)
  getAdditionalServices: () =>
    request<{ services: Record<string, unknown>[] }>('GET', '/additional-services'),
  createAdditionalService: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('POST', '/additional-services', data),
  updateAdditionalService: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('PUT', `/additional-services/${id}`, data),
  deleteAdditionalService: (id: number) => request<void>('DELETE', `/additional-services/${id}`),
  getAdditionalServiceBookings: (serviceId: number) =>
    request<{ bookings: Record<string, unknown>[] }>(
      'GET',
      `/additional-service-reservations?serviceId=${serviceId}`,
    ),
  createAdditionalServiceBooking: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('POST', '/additional-service-reservations', data),
  updateAdditionalServiceBooking: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('PUT', `/additional-service-reservations/${id}`, data),
  deleteAdditionalServiceBooking: (id: number) =>
    request<void>('DELETE', `/additional-service-reservations/${id}`),
  getAdditionalServiceSlots: (serviceId: number) =>
    request<{ slots: Record<string, unknown>[] }>('GET', `/additional-services/${serviceId}/slots`),
  createAdditionalServiceSlot: (serviceId: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('POST', `/additional-services/${serviceId}/slots`, data),
  updateAdditionalServiceSlot: (slotId: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('PUT', `/additional-service-slots/${slotId}`, data),
  deleteAdditionalServiceSlot: (slotId: number) =>
    request<void>('DELETE', `/additional-service-slots/${slotId}`),

  // ── Analytics & usage
  getAnalytics: () => request<Record<string, unknown>>('GET', '/analytics'),
  getUsage: (months?: number) => request<{ usage: Record<string, unknown>[] }>('GET', `/usage${months ? `?months=${months}` : ''}`),

  // ── Modules
  getModules: () => request<{ modules: Record<string, unknown>[] }>('GET', '/modules'),
  updateModule: (module: string, data: Record<string, unknown>) => request<Record<string, unknown>>('PUT', `/modules/${module}`, data),

  // ── Conversations
  getConversations: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<{ conversations: Record<string, unknown>[] }>('GET', `/conversations${qs}`);
  },
  getConversation: (id: number) => request<Record<string, unknown>>('GET', `/conversations/${id}`),

  // ── Organization (org_admin+, no marina scoping)
  getOrg: () => request<Record<string, unknown>>('GET', '/org', undefined, true),
  /** Setup checklist: saved flags + heuristics from the active marina (?marinaId=). All admin roles may read. */
  getOnboarding: () =>
    request<{
      onboarding_progress: Record<string, boolean>;
      detected: Record<string, boolean>;
      can_edit: boolean;
    }>('GET', '/onboarding', undefined, false),
  updateOrg: (data: Record<string, unknown>) => request<Record<string, unknown>>('PUT', '/org', data, true),
  getOrgMarinas: () => request<{ marinas: Record<string, unknown>[] }>('GET', '/org/marinas', undefined, true),
  createOrgMarina: (data: Record<string, unknown>) => request<Record<string, unknown>>('POST', '/org/marinas', data, true),
  updateOrgMarina: (marinaId: number, data: { name?: string; slug?: string }) =>
    request<Record<string, unknown>>('PUT', `/org/marinas/${marinaId}`, data, true),
  deleteOrgMarina: (marinaId: number) =>
    request<void>('DELETE', `/org/marinas/${marinaId}`, undefined, true),
  getOrgOverview: () => request<Record<string, unknown>>('GET', '/org/overview', undefined, true),
  getOrgUsage: () => request<Record<string, unknown>>('GET', '/org/usage', undefined, true),

  // ── Team management (no marina scoping — uses own header logic)
  getTeam: () => request<{ members: Record<string, unknown>[]; pending_invitations: Record<string, unknown>[] }>('GET', '/team'),
  inviteUser: (data: Record<string, unknown>) => request<Record<string, unknown>>('POST', '/team/invite', data),
  removeUser: (userId: string) => request<void>('DELETE', `/team/${userId}`),
  changeUserRole: (userId: string, role: string) => request<Record<string, unknown>>('PUT', `/team/${userId}/role`, { role }),
  revokeInvitation: (id: number) => request<void>('DELETE', `/team/invitations/${id}`),

  getAnalyticsDetailed: () => request<Record<string, unknown>>('GET', '/analytics/detailed'),

  // ── Billing (org_admin+, no marina scoping)
  getBilling: () => request<Record<string, unknown>>('GET', '/billing', undefined, true),
  /** Pull tier / max_marinas from Stripe (same response shape as getBilling). */
  reconcileBilling: () => request<Record<string, unknown>>('POST', '/billing/reconcile', {}, true),
  createCheckoutSession: (priceId: string, keepMarinaIds?: number[]) =>
    request<{ checkoutUrl: string | null; subscriptionUpdated?: boolean }>('POST', '/billing/checkout', { priceId, keepMarinaIds }, true),
  getBillingPortal: () =>
    request<{ portalUrl: string }>('GET', '/billing/portal', undefined, true),
  setMarinaActive: (marinaId: number, is_active: boolean) =>
    request<{ id: number; is_active: boolean }>('PUT', `/org/marinas/${marinaId}/active`, { is_active }, true),

  // ── Payments / Stripe Connect
  getPaymentStatus: () => request<{
    connected: boolean;
    onboarding_complete: boolean;
    payment_currency: string;
    platform_fee_percent: number;
    cancellation_policy: Record<string, number> | null;
  }>('GET', '/payments/status'),
  connectStripe: () => request<{ url: string }>('POST', '/payments/connect'),
  disconnectStripe: () => request<{ success: boolean }>('DELETE', '/payments/disconnect'),
  updatePaymentPolicy: (data: { cancellation_policy?: Record<string, number>; payment_currency?: string }) =>
    request<{ success: boolean }>('PUT', '/payments/policy', data),
  refundBooking: (bookingId: string, amountCents?: number) =>
    request<{ success: boolean; refunded_cents: number; refunded_amount: number; currency: string }>(
      'POST', `/payments/bookings/${bookingId}/refund`, amountCents !== undefined ? { amount_cents: amountCents } : {},
    ),
};

// ── Superadmin API (platform_superadmin only) ─────────────────

async function superadminRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getAuthToken();
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (body) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${API_BASE}/api/v1/superadmin${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 || res.status === 403) {
    let message = res.status === 403 ? 'Access denied' : 'Session expired';
    if (res.status === 403) {
      try {
        const errBody = (await res.json()) as { error?: string };
        if (typeof errBody.error === 'string' && errBody.error.length > 0) {
          message = errBody.error;
        }
      } catch {
        /* keep default */
      }
    }
    throw new ApiError(message, res.status, res.status === 403 ? 'FORBIDDEN' : 'AUTH_EXPIRED');
  }
  if (res.status === 204) return undefined as T;

  let json: Record<string, unknown>;
  try { json = await res.json(); } catch {
    throw new ApiError(`Server error (${res.status})`, res.status, 'PARSE_ERROR');
  }
  if (!res.ok) throw new ApiError((json.error as string) ?? 'Request failed', res.status, (json.code as string) ?? 'API_ERROR');
  return json as T;
}

export type OrgRow = {
  id: number; name: string; slug: string; owner_email: string | null;
  subscription_tier: string; subscription_status: string;
  trial_ends_at: string | null; max_marinas: number; marina_count: number;
  is_active: boolean; created_at: string; country: string | null; last_active: string | null;
};
export type MarinaRow = {
  id: number; name: string; slug: string; is_active: boolean;
  stripe_onboarding_complete: boolean; payment_currency: string | null;
  organization_id: number; created_at: string;
  organizations: { name: string; subscription_tier: string; subscription_status: string } | null;
};
export type AuditLogEntry = {
  id: number; actor_id: string; actor_email: string; action: string;
  target_type: string | null; target_id: string | null;
  metadata: Record<string, unknown> | null; ip_address: string | null; created_at: string;
};

export const superadminApi = {
  getOverview: () => superadminRequest<Record<string, unknown>>('GET', '/overview'),

  getOrgs: (params?: { tier?: string; status?: string; page?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.tier) qs.set('tier', params.tier);
    if (params?.status) qs.set('status', params.status);
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return superadminRequest<{ orgs: OrgRow[]; total: number; page: number; limit: number }>('GET', `/orgs${q ? `?${q}` : ''}`);
  },

  getOrg: (orgId: number) =>
    superadminRequest<{ org: Record<string, unknown>; marinas: Record<string, unknown>[]; users: Record<string, unknown>[]; usage_this_month: { chats: number; claude_cost_eur: number } }>('GET', `/orgs/${orgId}`),

  updateOrg: (orgId: number, data: { subscription_tier?: string; subscription_status?: string; max_marinas?: number; is_active?: boolean }) =>
    superadminRequest<{ success: boolean }>('PUT', `/orgs/${orgId}`, data),

  impersonateOrg: (orgId: number) =>
    superadminRequest<{ impersonation_token: string; target_email: string; target_name: string; expires_at: string; redeem_url: string }>('POST', `/orgs/${orgId}/impersonate`),

  getMarinas: (params?: { page?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return superadminRequest<{ marinas: MarinaRow[]; total: number; page: number; limit: number }>('GET', `/marinas${q ? `?${q}` : ''}`);
  },

  getBilling: () =>
    superadminRequest<{ mrr_eur: number; arr_eur: number; active_subscriptions: number; trialing: number; tier_breakdown: Record<string, { active: number; trialing: number }> }>('GET', '/billing'),

  getUsage: () =>
    superadminRequest<{ by_month: Array<{ month: string; chats: number; messages: number; input_tokens: number; output_tokens: number; cost_eur: number }> }>('GET', '/usage'),

  getAuditLog: () =>
    superadminRequest<{ entries: AuditLogEntry[] }>('GET', '/audit-log'),
};

// ── Public invite API (no auth) ───────────────────────────────

export const inviteApi = {
  getInvite: (token: string) => publicRequest<Record<string, unknown>>('GET', `/invite/${token}`),
  acceptInvite: (token: string, data: { name: string; password: string }) =>
    publicRequest<Record<string, unknown>>('POST', `/invite/${token}/accept`, data),
};

// ── Public signup API (no auth) ───────────────────────────────

export const signupApi = {
  signup: (data: {
    marinaName: string;
    yourName: string;
    email: string;
    country: string;
    password: string;
  }) => publicRequest<{ success: boolean; session: { access_token: string; refresh_token: string } | null }>('POST', '/signup', data),
};
